import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Users, UserPlus, Trash2, Copy, Mail } from 'lucide-react';

// Phase 6 UI — Lightweight Team Seats ($2.99/seat). Owner-only page.

const Team = () => {
  const [seats, setSeats] = useState([]);
  const [billing, setBilling] = useState({
    seat_count: 0, unit_price: 2.99, monthly_total: 0,
    pending_seat_count: 0, past_due_seat_count: 0, plan_lookup: 'zita_member_seat',
  });
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, b] = await Promise.all([
        api.get('/team/seats'),
        api.get('/team/seats/billing'),
      ]);
      setSeats(s.data.seats || []);
      setBilling(b.data);
    } catch (e) {
      // If the account is a seat itself the API returns 403 — we show empty state.
      const status = e?.response?.status;
      if (status === 403) toast.info('Only the owner account can manage team seats.');
      else toast.error('Could not load team seats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }
    setCreating(true);
    try {
      const r = await api.post('/team/seats', {
        email: email.trim(),
        display_name: displayName.trim() || null,
      });
      if (r.data.invitation_sent) {
        toast.success(`Invitation sent to ${email}. They'll create their PIN from the email.`);
      } else {
        toast.warning(
          `Seat added, but the invitation email did not deliver (${r.data.invitation_email_error || 'unknown reason'}). Use "Resend invitation" below.`,
        );
      }
      setEmail(''); setDisplayName('');
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Failed to add seat');
    } finally {
      setCreating(false);
    }
  };

  const resend = async (seatUserId) => {
    try {
      const r = await api.post(`/team/seats/${seatUserId}/resend-invitation`);
      if (r.data.invitation_sent) {
        toast.success('Invitation email resent.');
      } else {
        toast.error(`Resend failed: ${r.data.error || 'unknown'}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    }
  };

  const revoke = async (seat) => {
    if (!window.confirm(`Revoke access for ${seat.email}?`)) return;
    try {
      await api.delete(`/team/seats/${seat.user_id}`);
      toast.success('Seat revoked');
      load();
    } catch (e) {
      toast.error('Revoke failed');
    }
  };

  const copyLogin = (seat) => {
    const owner = document.body?.dataset?.ownerEmail || '';
    const url = `${window.location.origin}/login?seat=${encodeURIComponent(seat.email)}${owner ? `&owner=${encodeURIComponent(owner)}` : ''}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Sign-in link copied'));
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto space-y-6" data-testid="team-page">
      <header className="flex items-center gap-3">
        <div className="rounded-xl bg-orange-100 p-2 border border-orange-200">
          <Users className="text-orange-600" size={22} />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Team Seats</h1>
          <p className="text-sm text-slate-600">
            $2.99/seat/month billed on your Zita Pro card. Seats activate after Square confirms payment.
          </p>
        </div>
      </header>

      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5 flex items-center gap-4"
           data-testid="team-billing-summary">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Monthly cost</div>
          <div className="text-3xl font-bold text-slate-900 tabular-nums">
            ${billing.monthly_total?.toFixed(2) ?? '0.00'}
          </div>
        </div>
        <div className="text-xs text-slate-600 flex-1">
          <div>
            {billing.seat_count ?? 0} active seat{(billing.seat_count ?? 0) === 1 ? '' : 's'} × ${billing.unit_price?.toFixed(2) ?? '2.99'}
          </div>
          {(billing.pending_seat_count ?? 0) > 0 && (
            <div className="text-amber-700 mt-1" data-testid="team-pending-count">
              {billing.pending_seat_count} pending Square confirmation
            </div>
          )}
          {(billing.past_due_seat_count ?? 0) > 0 && (
            <div className="text-red-700 mt-1" data-testid="team-past-due-count">
              {billing.past_due_seat_count} paused — payment failed
            </div>
          )}
        </div>
      </div>

      <form
        onSubmit={create}
        className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 space-y-3"
        data-testid="team-seat-create-form"
      >
        <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Invite a seat</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@company.com"
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm"
            data-testid="seat-email-input"
          />
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Display name (optional)"
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm"
            data-testid="seat-name-input"
          />
        </div>
        <p className="text-[11px] text-slate-500">
          Zita will email them a secure invitation link. They&apos;ll choose their own PIN — you never have to share one.
        </p>
        <button
          type="submit"
          disabled={creating}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 disabled:opacity-50"
          data-testid="seat-save-btn"
        >
          <UserPlus size={14} /> {creating ? 'Sending invite…' : 'Send invitation'}
        </button>
      </form>

      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Active seats</div>
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : seats.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 p-6 text-center text-sm text-slate-500"
               data-testid="seats-empty">
            No seats yet. Add one above.
          </div>
        ) : (
          <div className="space-y-2" data-testid="seats-list">
            {seats.map((s) => (
              <div key={s.id}
                   className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3"
                   data-testid={`seat-row-${s.user_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    {s.display_name || s.email}
                    {s.active ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        Active
                      </span>
                    ) : s.pending_activation ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                            data-testid={`seat-pending-${s.user_id}`}>
                        Pending Square confirmation
                      </span>
                    ) : s.last_payment_failed_at ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200"
                            data-testid={`seat-past-due-${s.user_id}`}>
                        Payment failed
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                        Revoked
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {s.email}
                    {s.square_current_period_end && s.active && (
                      <> · billed through {String(s.square_current_period_end).slice(0, 10)}</>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => copyLogin(s)}
                  className="text-slate-400 hover:text-slate-700"
                  title="Copy sign-in link"
                  data-testid={`seat-copy-${s.user_id}`}
                >
                  <Copy size={16} />
                </button>
                {(s.pending_activation || !s.active) && !s.revoked_at && (
                  <button
                    onClick={() => resend(s.user_id)}
                    className="text-slate-400 hover:text-orange-600"
                    title="Resend invitation email"
                    data-testid={`seat-resend-${s.user_id}`}
                  >
                    <Mail size={16} />
                  </button>
                )}
                {s.active && (
                  <button
                    onClick={() => revoke(s)}
                    className="text-slate-400 hover:text-red-600"
                    title="Revoke"
                    data-testid={`seat-revoke-${s.user_id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Team;
